import { create } from 'zustand'
import { createAuthProvider } from '../auth/authProviderFactory'
import type { AuthUser } from '../auth/types'
import { NewPasswordRequiredError } from '../auth/types'

interface AuthState {
  isAuthenticated: boolean
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  /** Absolute Unix timestamp (seconds) when the access token expires. */
  tokenExpiresAt: number | null
  /** True while a NEW_PASSWORD_REQUIRED challenge is active. */
  pendingNewPassword: boolean
  /** The email used at login — retained for display during the challenge step. */
  pendingEmail: string | null
  login: (email: string, password: string) => Promise<void>
  completeNewPassword: (newPassword: string) => Promise<void>
  cancelNewPassword: () => void
  logout: () => Promise<void>
  refreshTokens: () => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  tokenExpiresAt: null,
  pendingNewPassword: false,
  pendingEmail: null,

  login: async (email: string, password: string): Promise<void> => {
    const provider = await createAuthProvider()
    try {
      const { tokens, user } = await provider.login({ email, password })
      set({
        isAuthenticated: true,
        user,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenExpiresAt: Math.floor(Date.now() / 1000) + tokens.expiresIn,
      })
    } catch (err) {
      if (err instanceof NewPasswordRequiredError) {
        // Challenge received — switch to new-password step; do not set isAuthenticated
        set({ pendingNewPassword: true, pendingEmail: email })
        return
      }
      throw err
    }
  },

  completeNewPassword: async (newPassword: string): Promise<void> => {
    const provider = await createAuthProvider()
    try {
      const { tokens, user } = await provider.completeNewPassword(newPassword)
      set({
        isAuthenticated: true,
        user,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenExpiresAt: Math.floor(Date.now() / 1000) + tokens.expiresIn,
        pendingNewPassword: false,
        pendingEmail: null,
      })
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === 'Your session has expired. Please sign in again.'
      ) {
        // Challenge session expired — return user to the credential form
        set({ pendingNewPassword: false, pendingEmail: null })
      }
      // Re-throw so the UI can display the error (policy violation keeps form open)
      throw err
    }
  },

  cancelNewPassword: (): void => {
    set({ pendingNewPassword: false, pendingEmail: null })
  },

  logout: async (): Promise<void> => {
    const { refreshToken } = get()
    // Clear local state unconditionally — best-effort revocation
    set({ isAuthenticated: false, user: null, accessToken: null, refreshToken: null, tokenExpiresAt: null })
    if (refreshToken) {
      try {
        await (await createAuthProvider()).logout(refreshToken)
      } catch {
        // best-effort: ignore provider errors
      }
    }
  },

  refreshTokens: async (): Promise<void> => {
    const { refreshToken } = get()
    const provider = await createAuthProvider()
    const tokens = await provider.refresh(refreshToken ?? '')
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      tokenExpiresAt: Math.floor(Date.now() / 1000) + tokens.expiresIn,
    })
  },
}))
